import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import styled from 'styled-components';
import { message, Spin, Pagination } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { SubColor } from '../style/styleValue';
import { 
  getQnAList, registerQnA, updateQnA, deleteQnA, getQnAData, 
  registerAnswer, updateAnswer, deleteAnswer 
} from '../api/qna';
import { getLoginUser } from '../api/user';

interface User {
  username: string;
  role: string;
}

interface PostListItem {
  id: number;
  title: string;
  authorName: string;
  createdAt: string;
  viewCount: number;
}

interface Answer {
  id: number;
  content: string;
  questionId: number;
  authorName: string;
  authorId: number;
  createdAt: string;
  modifiedAt: string;
}

interface PostDetail {
  id: number;
  title: string;
  content: string;
  authorName: string;
  viewCount: number;
  createdAt: string;
  modifiedAt: string;
  answers: Answer[];
}

const QnA = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryId = searchParams.get('id');
  const queryPage = searchParams.get('page');
  
  const [currentUser, setCurrentUser] = useState<User>({ username: "", role: "" });
  const [posts, setPosts] = useState<PostListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [page, setPage] = useState(queryPage ? Number(queryPage) : 1);
  const [pageSize, setPageSize] = useState(10);

  const [isWriteModalOpen, setIsWriteModalOpen] = useState(false);
  const [viewPost, setViewPost] = useState<PostDetail | null>(null);
  
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ title: '', content: '' });

  const [newAnswerContent, setNewAnswerContent] = useState('');
  const [editingAnswerId, setEditingAnswerId] = useState<number | null>(null);
  const [editingAnswerContent, setEditingAnswerContent] = useState('');

  useEffect(() => {
    if (queryPage) {
      setPage(Number(queryPage));
    }
  }, [queryPage]);

  const fetchPosts = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await getQnAList(page, pageSize);
      setPosts(response.data); 
      setTotalCount(response.total); 
    } catch (error) {
      message.error('게시글 목록을 불러오는데 실패했습니다.');
    } finally {
      setListLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    if (queryId) {
      const fetchDetail = async () => {
        setDetailLoading(true);
        try {
          const data = await getQnAData(Number(queryId));
          setViewPost(data);
          setNewAnswerContent('');
          setEditingAnswerId(null);
        } catch (error) {
          console.error(error);
          message.error('상세 내용을 불러오는데 실패했습니다.');
          setSearchParams({ page: String(page) });
        } finally {
          setDetailLoading(false);
        }
      };
      fetchDetail();
    } else {
      setViewPost(null);
    }
  }, [queryId, setSearchParams]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const fetchPostDetail = async (id: number) => {
    try {
      const data = await getQnAData(id);
      setViewPost(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handlePostClick = (post: PostListItem) => {
    setSearchParams({ page: String(page), id: String(post.id), qnaTitle: String(post.title) });
  };

  const handleBackToList = () => {
    setSearchParams({ page: String(page) });
  };

  const handlePageChange = (newPage: number) => {
    setSearchParams({ page: String(newPage) });
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setSearchParams({ page: '1' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleWriteClick = () => {
    setEditingId(null);
    setFormData({ title: '', content: '' });
    setIsWriteModalOpen(true);
  };

  const handleEditFromDetail = () => {
    if (!viewPost) return;
    setEditingId(viewPost.id);
    setFormData({ title: viewPost.title, content: viewPost.content });
    setIsWriteModalOpen(true);
  };

  const closeWriteModal = () => {
    setIsWriteModalOpen(false);
    setEditingId(null);
    setFormData({ title: '', content: '' });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      message.warning('제목과 내용을 모두 입력해주세요.');
      return;
    }

    if (editingId) {
      const result = await updateQnA(editingId, formData.title, formData.content);
      if (result) {
        message.success('게시글이 수정되었습니다.');
        if (viewPost && viewPost.id === editingId) {
          await fetchPostDetail(editingId);
        } else {
          fetchPosts();
        }
        closeWriteModal();
      } else {
        message.error('게시글 수정에 실패했습니다.');
      }
    } else {
      const result = await registerQnA(formData.title, formData.content);
      if (result) {
        message.success('게시글이 등록되었습니다.');
        fetchPosts(); 
        closeWriteModal();
      } else {
        message.error('게시글 등록에 실패했습니다.');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('정말로 이 게시글을 삭제하시겠습니까?')) {
      const result = await deleteQnA(id);
      if (result) {
        message.success('게시글이 삭제되었습니다.');
        handleBackToList();
        fetchPosts();
      } else {
        message.error('게시글 삭제에 실패했습니다.');
      }
    }
  };

  const handleRegisterAnswer = async () => {
    if (!viewPost) return;
    if (!newAnswerContent.trim()) {
      message.warning('답변 내용을 입력해주세요.');
      return;
    }
    const result = await registerAnswer(viewPost.id, newAnswerContent);
    if (result) {
      message.success('답변이 등록되었습니다.');
      setNewAnswerContent('');
      await fetchPostDetail(viewPost.id);
    } else {
      message.error('답변 등록에 실패했습니다.');
    }
  };

  const handleDeleteAnswer = async (answerId: number) => {
    if (!viewPost) return;
    if (window.confirm('정말로 이 답변을 삭제하시겠습니까?')) {
      const result = await deleteAnswer(answerId);
      if (result) {
        message.success('답변이 삭제되었습니다.');
        await fetchPostDetail(viewPost.id);
      } else {
        message.error('답변 삭제에 실패했습니다.');
      }
    }
  };

  const startEditAnswer = (answer: Answer) => {
    setEditingAnswerId(answer.id);
    setEditingAnswerContent(answer.content);
  };

  const cancelEditAnswer = () => {
    setEditingAnswerId(null);
    setEditingAnswerContent('');
  };

  const handleUpdateAnswer = async () => {
    if (!viewPost || !editingAnswerId) return;
    if (!editingAnswerContent.trim()) {
      message.warning('답변 내용을 입력해주세요.');
      return;
    }
    const result = await updateAnswer(editingAnswerId, editingAnswerContent);
    if (result) {
      message.success('답변이 수정되었습니다.');
      setEditingAnswerId(null);
      await fetchPostDetail(viewPost.id);
    } else {
      message.error('답변 수정에 실패했습니다.');
    }
  };

  useEffect(() => {
    getLoginUser().then(data => setCurrentUser({ username: data.name, role: data.role }));
  }, []);

  return (
    <Container>
      {!queryId && !detailLoading ? (
        <>
          <TopBar>
            <TotalCount>총 <span>{totalCount}</span>개</TotalCount>
            <SelectBox onChange={handlePageSizeChange} value={pageSize}>
              <option value="10">10개</option>
              <option value="20">20개</option>
              <option value="30">30개</option>
            </SelectBox>
          </TopBar>

          <Table>
            <thead>
              <tr>
                <Th width="8%">번호</Th>
                <Th width="50%">제목</Th>
                <Th width="15%">작성자</Th>
                <Th width="15%">등록일</Th>
                <Th width="12%">조회</Th>
              </tr>
            </thead>
            <tbody>
              {listLoading ? (
                 <tr><Td colSpan={5} style={{ padding: '50px' }}><Spin /></Td></tr>
              ) : posts?.length > 0 ? (
                posts?.map((post) => (
                  <tr key={post.id}>
                    <Td>{post.id}</Td>
                    <Td alignLeft onClick={() => handlePostClick(post)}>
                      {post.title}
                    </Td>
                    <Td>{post.authorName}</Td>
                    <Td>{post.createdAt.substring(0, 10)}</Td>
                    <Td>{post.viewCount}</Td>
                  </tr>
                ))
              ) : (
                <tr>
                  <Td colSpan={5} style={{ padding: '50px 0', color: '#888' }}>
                    등록된 게시물이 없습니다.
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
          <BottomArea>
              <PaginationWrapper>
                 {totalCount > 0 && (
                   <Pagination 
                     current={page}
                     total={totalCount}
                     pageSize={pageSize}
                     onChange={handlePageChange}
                     showSizeChanger={false}
                   />
                 )}
              </PaginationWrapper>
            <WriteButton onClick={handleWriteClick}>글쓰기</WriteButton>
          </BottomArea>
        </>
      ) : detailLoading ? (
        <LoadingContainer>
          <Spin size="large" tip="상세 내용을 불러오는 중입니다..." />
        </LoadingContainer>
      ) : viewPost ? (
        <DetailContainer>
          <DetailHeader>
            <div className="top-row">
              <BackButton onClick={handleBackToList}>
                <ArrowLeftOutlined /> 목록으로
              </BackButton>
              <DetailActions>
                {(currentUser.role === 'ADMIN_MAIN' || currentUser.username === viewPost.authorName) && (
                  <>
                    <Button onClick={handleEditFromDetail}>수정</Button>
                    <ActionButton isDelete onClick={() => handleDelete(viewPost.id)}>삭제</ActionButton>
                  </>
                )}
              </DetailActions>
            </div>
            <DetailTitle>{viewPost.title}</DetailTitle>
            <DetailMeta>
              <span>작성자: {viewPost.authorName}</span>
              <span className="divider">|</span>
              <span>{viewPost.createdAt.substring(0, 10)}</span>
              <span className="divider">|</span>
              <span>조회수: {viewPost.viewCount}</span>
            </DetailMeta>
          </DetailHeader>

          <DetailContent>
            {viewPost.content}
          </DetailContent>

          <AnswerSection>
            <ViewLabel>답변 ({viewPost.answers.length})</ViewLabel>
            
            {viewPost.answers.length > 0 ? (
              viewPost.answers.map((answer) => (
                <AnswerCard key={answer.id}>
                  {editingAnswerId === answer.id ? (
                    <AnswerEditBox>
                      <StyledTextArea 
                        value={editingAnswerContent}
                        onChange={(e) => setEditingAnswerContent(e.target.value)}
                        rows={3}
                        style={{ marginBottom: '8px' }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px' }}>
                        <SmallButton onClick={cancelEditAnswer}>취소</SmallButton>
                        <SmallButton primary onClick={handleUpdateAnswer}>저장</SmallButton>
                      </div>
                    </AnswerEditBox>
                  ) : (
                    <>
                      <AnswerHeader>
                        <div>
                          <span className="author">{answer.authorName}</span>
                          <span className="date"> | {answer.createdAt.substring(0, 10)}</span>
                        </div>
                        {(currentUser.role === 'ADMIN_MAIN' || currentUser.username === answer.authorName) && (
                          <AnswerActions>
                            <span onClick={() => startEditAnswer(answer)}>수정</span>
                            <span onClick={() => handleDeleteAnswer(answer.id)}>삭제</span>
                          </AnswerActions>
                        )}
                      </AnswerHeader>
                      <AnswerContent>{answer.content}</AnswerContent>
                    </>
                  )}
                </AnswerCard>
              ))
            ) : (
              <NoAnswer>아직 답변이 등록되지 않았습니다.</NoAnswer>
            )}

            {currentUser.role === 'ADMIN_MAIN' && (
              <AnswerInputArea>
                <Label>답변 작성</Label>
                <StyledTextArea 
                  value={newAnswerContent}
                  onChange={(e) => setNewAnswerContent(e.target.value)}
                  rows={3}
                  placeholder="답변 내용을 입력하세요"
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <Button primary onClick={handleRegisterAnswer}>답변 등록</Button>
                </div>
              </AnswerInputArea>
            )}
          </AnswerSection>
        </DetailContainer>
      ) : null}

      {isWriteModalOpen && (
         <ModalOverlay onClick={closeWriteModal}>
            <ModalBox onClick={(e) => e.stopPropagation()}>
               <ModalHeader>
                  <h3>{editingId ? "게시글 수정" : "게시글 작성"}</h3>
                  <CloseButton onClick={closeWriteModal}>&times;</CloseButton>
               </ModalHeader>
               <ModalBody>
                  <Label>제목</Label>
                  <StyledInput 
                     name="title"
                     value={formData.title} 
                     onChange={handleInputChange} 
                     placeholder="제목을 입력하세요"
                  />
                  <Label>내용</Label>
                  <StyledTextArea 
                     name="content"
                     value={formData.content} 
                     onChange={handleInputChange} 
                     rows={8}
                     placeholder="문의 내용을 입력하세요"
                  />
               </ModalBody>
               <ModalFooter>
                  <Button onClick={closeWriteModal}>취소</Button>
                  <Button primary onClick={handleSubmit}>{editingId ? "수정" : "등록"}</Button>
               </ModalFooter>
            </ModalBox>
         </ModalOverlay>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: 'Noto Sans KR', sans-serif;
  min-height: 500px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  width: 100%;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const TotalCount = styled.div`
  font-size: 14px;
  color: #333;
  span { color: ${SubColor}; font-weight: bold; }
`;

const SelectBox = styled.select`
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  outline: none;
  cursor: pointer;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-top: 2px solid ${SubColor};
`;

const Th = styled.th<{ width?: string }>`
  padding: 15px 10px;
  background-color: #f9f9f9;
  border-bottom: 1px solid #ddd;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  text-align: center;
  width: ${(props) => props.width || 'auto'};
`;

const Td = styled.td<{ alignLeft?: boolean }>`
  padding: 15px 10px;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  color: #555;
  text-align: ${(props) => (props.alignLeft ? 'left' : 'center')};
  &:hover {
    color: ${(props) => (props.alignLeft ? SubColor : '#555')};
    font-weight: ${(props) => (props.alignLeft ? '600' : 'normal')};
    cursor: ${(props) => (props.alignLeft ? 'pointer' : 'default')};
    text-decoration: ${(props) => (props.alignLeft ? 'underline' : 'none')};
  }
`;

const BottomArea = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  position: relative;
`;

const PaginationWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const WriteButton = styled.button`
  padding: 10px 24px;
  background-color: #fff;
  border: 1px solid ${SubColor};
  color: ${SubColor};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
  &:hover { background-color: ${SubColor}; color: #fff; }
`;

const DetailContainer = styled.div`
  background-color: #fff;
  border-radius: 8px;
  animation: fadeIn 0.3s;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

const DetailHeader = styled.div`
  border-bottom: 1px solid #eee;
  padding-bottom: 20px;
  margin-bottom: 30px;

  .top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 15px;
  color: #555;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  font-weight: 500;
  
  &:hover { color: ${SubColor}; }
`;

const DetailActions = styled.div`
  display: flex;
  gap: 8px;
`;

const DetailTitle = styled.h2`
  font-size: 24px;
  color: #333;
  margin: 0 0 10px 0;
  font-weight: 700;
`;

const DetailMeta = styled.div`
  font-size: 14px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 10px;
  .divider { color: #ddd; font-size: 12px; }
`;

const DetailContent = styled.div`
  font-size: 16px;
  color: #333;
  line-height: 1.8;
  white-space: pre-wrap;
  min-height: 200px;
  margin-bottom: 50px;
  padding: 10px;
`;

const AnswerSection = styled.div`
  margin-top: 40px;
  border-top: 2px solid #f0f0f0;
  padding-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ViewLabel = styled.span`
  font-size: 16px;
  color: #333;
  font-weight: 700;
  margin-bottom: 10px;
  display: block;
`;

const AnswerCard = styled.div`
  background-color: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 20px;
`;

const AnswerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 14px;
  .author { font-weight: 600; color: ${SubColor}; }
  .date { color: #999; }
`;

const AnswerActions = styled.div`
  font-size: 13px;
  color: #999;
  display: flex;
  gap: 10px;
  span {
    cursor: pointer;
    &:hover { text-decoration: underline; color: #333; }
  }
`;

const AnswerContent = styled.div`
  font-size: 15px;
  color: #444;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const NoAnswer = styled.div`
  text-align: center;
  color: #999;
  padding: 30px;
  font-size: 15px;
  background: #f9f9f9;
  border-radius: 6px;
`;

const AnswerInputArea = styled.div`
  margin-top: 15px;
  padding: 20px;
  background-color: #fff;
  border: 1px solid ${SubColor};
  border-radius: 8px;
`;

const AnswerEditBox = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActionButton = styled.button<{ isDelete?: boolean }>`
  padding: 8px 16px;
  font-size: 14px;
  border: 1px solid #ddd;
  background-color: #fff;
  color: ${(props) => (props.isDelete ? '#ff4d4f' : '#333')};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  &:hover {
    border-color: ${(props) => (props.isDelete ? '#ff4d4f' : SubColor)};
    color: ${(props) => (props.isDelete ? '#fff' : SubColor)};
    background-color: ${(props) => (props.isDelete ? '#ff4d4f' : '#fff')};
  }
`;

const Button = styled.button<{ primary?: boolean }>`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${(props) => (props.primary ? SubColor : '#ddd')};
  background-color: ${(props) => (props.primary ? SubColor : '#fff')};
  color: ${(props) => (props.primary ? '#fff' : '#333')};
  transition: all 0.2s;
  &:hover { opacity: 0.9; background-color: ${(props) => (props.primary ? SubColor : '#f0f0f0')}; }
`;

const SmallButton = styled(Button)`
  padding: 4px 10px;
  font-size: 12px;
`;

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex; justify-content: center; align-items: center; z-index: 1000;
`;

const ModalBox = styled.div`
  background: white; width: 600px; max-width: 90%; max-height: 90vh;
  border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  display: flex; flex-direction: column; overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 16px 24px; border-bottom: 1px solid #eee;
  display: flex; justify-content: space-between; align-items: center;
  h3 { margin: 0; font-size: 18px; font-weight: 600; color: #333; }
`;

const CloseButton = styled.button`
  background: none; border: none; font-size: 24px; cursor: pointer; color: #999;
  &:hover { color: #333; }
`;

const ModalBody = styled.div`
  padding: 24px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px; flex: 1;
`;

const ModalFooter = styled.div`
  padding: 16px 24px; border-top: 1px solid #eee; display: flex; justify-content: flex-end; gap: 10px; background-color: #f9f9f9;
`;

const Label = styled.label`
  font-size: 14px; font-weight: 600; color: #333; margin-bottom: 4px; display: block;
`;

const StyledInput = styled.input`
  width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; outline: none; box-sizing: border-box;
  &:focus { border-color: ${SubColor}; }
`;

const StyledTextArea = styled.textarea`
  width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; outline: none; resize: vertical; box-sizing: border-box;
  &:focus { border-color: ${SubColor}; }
`;

export default QnA;